import type {
  AuthProvider,
  Leaderboard,
  LeaderboardEntry,
  Player,
  RunResult,
  ScoreStore,
  Services,
} from '../types';
import { readJson, writeJson } from './storage';

const KEY = {
  player: 'cs.player',
  runs: (mode: string) => `cs.runs.${mode}`,
  board: (mode: string) => `cs.board.${mode}`,
};

function guestId(): string {
  // crypto.randomUUID is widely available; fall back to a timestamp-ish id.
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `guest-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

class LocalAuth implements AuthProvider {
  async current(): Promise<Player | null> {
    return readJson<Player | null>(KEY.player, null);
  }
  async signIn(name?: string): Promise<Player> {
    const existing = readJson<Player | null>(KEY.player, null);
    const player: Player = existing ?? { id: guestId(), name: name ?? 'Guest', guest: true };
    if (name) player.name = name;
    writeJson(KEY.player, player);
    return player;
  }
  async signOut(): Promise<void> {
    writeJson(KEY.player, null);
  }
}

class LocalScoreStore implements ScoreStore {
  async getBest(mode: string): Promise<number> {
    return readJson<RunResult[]>(KEY.runs(mode), []).reduce((m, r) => Math.max(m, r.score), 0);
  }
  async record(result: RunResult): Promise<void> {
    const runs = readJson<RunResult[]>(KEY.runs(result.mode), []);
    runs.push(result);
    runs.sort((a, b) => b.score - a.score);
    writeJson(KEY.runs(result.mode), runs.slice(0, 50));
  }
  async history(mode: string, limit = 10): Promise<RunResult[]> {
    return readJson<RunResult[]>(KEY.runs(mode), []).slice(0, limit);
  }
}

// A local stand-in for a global board: just this device's best runs. The shape
// matches the real Leaderboard so the Cloudflare version slots in unchanged.
class LocalLeaderboard implements Leaderboard {
  async top(mode: string, limit = 10): Promise<LeaderboardEntry[]> {
    return readJson<LeaderboardEntry[]>(KEY.board(mode), [])
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  }
  async submit(entry: LeaderboardEntry): Promise<void> {
    const board = readJson<LeaderboardEntry[]>(KEY.board(entry.mode), []);
    board.push(entry);
    board.sort((a, b) => b.score - a.score);
    writeJson(KEY.board(entry.mode), board.slice(0, 100));
  }
}

export function createLocalServices(): Services {
  return {
    auth: new LocalAuth(),
    scores: new LocalScoreStore(),
    leaderboard: new LocalLeaderboard(),
  };
}
